import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

export interface ArticlePart {
  kind: 'markdown' | 'video' | 'videofile';
  markdown?: string;
  video?: SafeResourceUrl;
  videoSrc?: string;
}

/**
 * Splits Notion-sourced markdown into markdown + video parts, rendering standalone video references as
 * embedded players: backend [[VIDEO:url]] / [[VIDEOFILE:url]] tokens AND standalone YouTube/Vimeo/Loom
 * markdown links/images (videos are usually authored as a "[Video](https://youtube…)" link, not a Notion
 * video block). Shared by the help-center article renderer and the release-notes sidebar.
 */
export function splitMarkdownIntoParts(markdown: string | null | undefined, sanitizer: DomSanitizer): ArticlePart[] {
  if (!markdown) {
    return [];
  }
  const parts: ArticlePart[] = [];
  let buffer: string[] = [];
  const flush = (): void => {
    const text = buffer.join('\n');
    if (text.trim().length > 0) {
      parts.push({ kind: 'markdown', markdown: text });
    }
    buffer = [];
  };

  for (const line of markdown.split('\n')) {
    const fileUrl = lineToVideoFileUrl(line);
    const embedUrl = fileUrl ? null : lineToVideoEmbedUrl(line);
    if (fileUrl) {
      flush();
      parts.push({ kind: 'videofile', videoSrc: fileUrl });
    } else if (embedUrl) {
      flush();
      parts.push({ kind: 'video', video: sanitizer.bypassSecurityTrustResourceUrl(embedUrl) });
    } else {
      buffer.push(line);
    }
  }
  flush();
  return parts;
}

/** If a whole line is a [[VIDEOFILE:url]] token (a Notion-uploaded video re-hosted to our CDN), returns the URL. */
function lineToVideoFileUrl(line: string): string | null {
  const m = /^\[\[VIDEOFILE:(.+?)\]\]$/.exec(line.trim());
  return m ? m[1] : null;
}

/** If a whole line is a video reference ([[VIDEO:url]] or a standalone video link/image), returns its embed URL. */
function lineToVideoEmbedUrl(line: string): string | null {
  const trimmed = line.trim();
  let url: string | null = null;
  const token = /^\[\[VIDEO:(.*?)\]\]$/.exec(trimmed);
  if (token) {
    url = token[1];
  } else {
    const link = /^!?\[[^\]]*\]\(([^)\s]+)\)$/.exec(trimmed);
    if (link) {
      url = link[1];
    }
  }
  return url ? toEmbedUrl(url) : null;
}

/**
 * Normalizes a YouTube / Vimeo / Loom URL to its embed form; returns null for any other host.
 *
 * These iframes render inside the authenticated portal, so use the privacy-preserving player variants:
 * `youtube-nocookie.com` and Vimeo's `dnt=1` both suppress the vendor's tracking cookies. Keep this in
 * sync with `BlockToMarkdownTransformer.toEmbedUrl` on the backend, which builds the same URLs for
 * Notion-authored articles — changing only one side leaves half the videos on the tracking host.
 */
function toEmbedUrl(url: string): string | null {
  // Also match youtube-nocookie.com: the backend's [[VIDEO:]] tokens already carry the
  // privacy-preserving embed URL, which must round-trip here rather than fall through as text.
  const yt = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube(?:-nocookie)?\.com\/embed\/)([A-Za-z0-9_-]{6,})/.exec(url);
  if (yt) {
    return `https://www.youtube-nocookie.com/embed/${yt[1]}`;
  }
  const vimeo = /vimeo\.com\/(?:video\/)?(\d+)/.exec(url);
  if (vimeo) {
    return `https://player.vimeo.com/video/${vimeo[1]}?dnt=1`;
  }
  const loom = /loom\.com\/(?:share|embed)\/([A-Za-z0-9]+)/.exec(url);
  if (loom) {
    return `https://www.loom.com/embed/${loom[1]}`;
  }
  return null;
}
